import { InfiniteData, QueryKey, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  AssetChatFeedItem,
  AssetChatFeedPage,
  AssetChatSubject,
} from '../../activities/domain';
import { useCurrentAccount } from '../../auth/hooks/useCurrentAccount';
import {
  IssueMessageType,
  LocationInput,
  ReportIssueInput,
  Severity,
} from '../../graphql';
import { Media } from '../../media/domain/media';
import {
  extractMentionsFromText,
  getMentionTextParts,
} from '../../utils/mentionsHelper';
import { newGUID } from '../../utils/newGuid';
import { extractTitleFromMessage, getIssueTypeFromSeverity } from '../domain';
import { useCreateIssueWithMessage } from './useCreateIssueWithMessage';

export type UseCreateChatActivityArgs = {
  /** The asset and workspace the post is filed against. */
  subject: Pick<AssetChatSubject, 'assetId' | 'workspaceId'>;
  /**
   * The chat feed's react-query key, from `useAssetChatFeed().cacheKey`.
   * Supplied so a post shows up before the server answers. Omit it and the
   * post simply appears when the create's own cache invalidation refetches.
   */
  feedCacheKey?: QueryKey;
};

export type CreateChatActivityArgs = {
  /** Raw composer text, mentions still in `@[name](id)` form. */
  message: string;
  /**
   * Defaults to purple, the plain message. Red/yellow/green come from the
   * composer's send-as picker and post an ordinary activity instead.
   *
   * Typed as `Severity` rather than `string` on purpose: `ReportIssueInput`
   * declares this field `String!` server-side and stores whatever it is
   * given, while `Issue.severity` reads back as an enum — so a label like
   * "Urgent" reaching here writes a document that fails every later read
   * (gripp-9ee). The picker must map its labels to enum members, and this
   * type is what forces it to.
   */
  severity?: Severity;
  /** Already-uploaded media; ids go to the server, the objects render the pending row. */
  media?: Media[];
  /** From `useCurrentLocation`, when the author shared their location. */
  location?: LocationInput;
};

type FeedCache = InfiniteData<AssetChatFeedPage> | undefined;

/**
 * Posting in an asset's chat. There is no chat-message model: a post is the
 * same `Issue` the classic activity list has always shown, created through the
 * same `reportIssue` mutation `NewIssue` uses, with `title` and `type` derived
 * by the two helpers both paths now share (`extractTitleFromMessage`,
 * `getIssueTypeFromSeverity`). A plain message is `{type: NOTE, severity:
 * purple}` — the purple note the classic view already renders; send-as
 * urgent/pending/solved is a `REPORT` at red/yellow/green, indistinguishable
 * from one filed through the report form.
 *
 * The new row is shown before the server answers. `reportIssue` returns only
 * `{ id }`, so there is nothing to insert from the response — the row is built
 * here from the composer's own inputs plus the current account, under a
 * placeholder id, and the returned id is swapped in on success. The create's
 * cache invalidation then refetches the feed and supersedes it; the reader
 * deduplicates by id (see `useAssetChatFeed`) so the two cannot both show.
 */
export const useCreateChatActivity = ({
  subject,
  feedCacheKey,
}: UseCreateChatActivityArgs) => {
  const queryClient = useQueryClient();
  const { account } = useCurrentAccount();
  const { mutateAsync, isPending } = useCreateIssueWithMessage();

  const updateFeed = useCallback(
    (update: (previous: FeedCache) => FeedCache) => {
      if (!feedCacheKey) return;
      queryClient.setQueryData<FeedCache>(feedCacheKey, update);
    },
    [feedCacheKey, queryClient]
  );

  const createChatActivity = useCallback(
    async ({ message, severity, media, location }: CreateChatActivityArgs) => {
      const postSeverity = severity ?? Severity.Purple;
      const mentions = extractMentionsFromText(message);

      const input: ReportIssueInput = {
        severity: postSeverity,
        title: extractTitleFromMessage(message),
        type: getIssueTypeFromSeverity(postSeverity),
        message,
        workspaceId: subject.workspaceId,
        assetId: subject.assetId,
        media: media?.length ? media.map((item) => item.id) : undefined,
        location,
        userMentions: mentions.length
          ? mentions.map((mention) => mention.id as string)
          : undefined,
      };

      const pendingId = newGUID();
      updateFeed((previous) =>
        insertRow(previous, buildPendingRow(pendingId, input, media, account))
      );

      try {
        const result = (await mutateAsync({ input })) as {
          issue: { reportIssue: { id: string } };
        };
        const createdId = result?.issue?.reportIssue?.id;

        // Usually a no-op: the mutation's own invalidation refetches the feed
        // before this runs, and the real row is already there. It matters when
        // that refetch is slower than the response — without the swap the row
        // would keep a placeholder id and read as a post with no thread.
        if (createdId) {
          updateFeed((previous) =>
            mapRows(previous, (row) =>
              row.id === pendingId
                ? { ...row, id: createdId, isPending: undefined }
                : row
            )
          );
        }

        return result;
      } catch (error) {
        updateFeed((previous) => removeRow(previous, pendingId));
        throw error;
      }
    },
    [account, mutateAsync, subject.assetId, subject.workspaceId, updateFeed]
  );

  return { createChatActivity, isPending };
};

/**
 * The row the feed shows while the post is in flight, in the shape
 * `findIssues` would return for it — the composer's own inputs are the only
 * source, since the mutation answers with an id and nothing else.
 */
const buildPendingRow = (
  pendingId: string,
  input: ReportIssueInput,
  media: Media[] | undefined,
  account: ChatAuthor | null
): AssetChatFeedItem => {
  const now = new Date().toISOString();
  const author = { id: account?.id ?? '', name: account?.name };

  return {
    id: pendingId,
    title: input.title,
    severity: input.severity as Severity,
    type: input.type,
    asset: { id: input.assetId },
    workspace: { id: input.workspaceId },
    createdAt: now,
    updatedAt: now,
    effectiveCreationDate: now,
    createdBy: author,
    userMentions: mentionActorsFromText(input.message),
    originMessage: {
      // A placeholder too: the message is written server-side by
      // `reportIssue`, so its real id is not knowable here.
      id: `${pendingId}-message`,
      text: input.message,
      type: IssueMessageType.UserMessage,
      createdBy: author,
      media,
    },
    replyCount: 0,
    isPending: true,
  };
};

/** The current account, as loosely as this row needs it (`Account.id` is optional). */
type ChatAuthor = { id?: string; name?: { first?: string; last?: string } };

/** One `getMentionTextParts` part; `name`/`guid` are present only on a mention. */
type MentionPart = {
  text: string;
  isMention: boolean;
  name?: string;
  guid?: string;
};

/**
 * Mention actors for the pending row, read back out of the text the author
 * just wrote — `@[Jane Doe](id)` carries both halves. The row's renderer joins
 * `first` and `last`, so the display name is split on its first space to
 * reproduce it; without these the row would show every mention as an unknown
 * user until the refetch lands.
 */
const mentionActorsFromText = (message: string) => {
  const parts: MentionPart[] = getMentionTextParts(message);
  return parts
    .filter((part) => part.isMention && !!part.guid)
    .map((part) => {
      const [first, ...rest] = (part.name ?? '').split(' ');
      return { id: part.guid as string, name: { first, last: rest.join(' ') } };
    });
};

/**
 * Page 0 holds the newest rows (the feed is `effectiveCreationDate` Desc), so
 * a new post goes at its head. An unfetched feed is left alone rather than
 * given a fabricated first page — its pagination state would be wrong, and
 * the invalidation refetch shows the post anyway.
 */
const insertRow = (previous: FeedCache, row: AssetChatFeedItem): FeedCache => {
  if (!previous?.pages?.length) return previous;
  const [first, ...rest] = previous.pages;
  return {
    ...previous,
    pages: [
      { ...first, items: [row, ...first.items], count: first.count + 1 },
      ...rest,
    ],
  };
};

const removeRow = (previous: FeedCache, rowId: string): FeedCache => {
  if (!previous?.pages?.length) return previous;
  return {
    ...previous,
    pages: previous.pages.map((page) => {
      const items = page.items.filter((item) => item.id !== rowId);
      return items.length === page.items.length
        ? page
        : { ...page, items, count: Math.max(0, page.count - 1) };
    }),
  };
};

const mapRows = (
  previous: FeedCache,
  map: (row: AssetChatFeedItem) => AssetChatFeedItem
): FeedCache => {
  if (!previous?.pages?.length) return previous;
  return {
    ...previous,
    pages: previous.pages.map((page) => ({
      ...page,
      items: page.items.map(map),
    })),
  };
};
